/*
 * CapacidadPagoComite.jsx — oct-2026
 *
 * Sección de SOLO LECTURA del diálogo del Comité de Crédito, arriba de «Decisión del
 * Comité»: documentos de ingresos del solicitante (los del expediente del cliente y los
 * cargados en la solicitud) con tipo, fecha de carga, vigencia y botón «Ver», más los datos
 * capturados que sirven para evaluar (ingreso mensual declarado, fuente de pago y destino
 * del crédito).
 *
 * El Comité SOLO VE: aquí no hay subir, reemplazar ni borrar. Lo que falta se muestra como
 * «No cargado» y NO bloquea la decisión.
 */
import { useState } from "react";
import { Wallet, FileDown, Loader2, FileX2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { labelDestinoCredito, labelFuentePago } from "@/lib/catalogos_maestros";
import { toast } from "sonner";

const MSG_FALLA = "No se pudo abrir el documento. Intenta de nuevo o avisa a soporte.";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Con `responseType: "blob"` el cuerpo del error también llega como Blob, así que el
// `detail` del backend hay que leerlo del texto antes de poder mostrarlo.
async function mensajeFalla(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const detalle = JSON.parse(await data.text())?.detail;
      if (typeof detalle === "string" && detalle) return detalle;
    } catch (_err) { /* cuerpo no JSON */ }
    return MSG_FALLA;
  }
  return apiError(e) || MSG_FALLA;
}

export function CapacidadPagoComite({ capacidad }) {
  const [abriendo, setAbriendo] = useState("");
  if (!capacidad) return null;
  const docs = capacidad.documentos || [];
  const d = capacidad.datos || {};
  const varias = new Set(docs.map((x) => x.cliente_id)).size > 1;

  // oct-2026 · Antes de mandar al Comité a otra pestaña, el documento se PRUEBA con la
  // sesión: si no se puede leer, la falla se avisa con un mensaje claro en la pantalla en
  // vez de dejar una pestaña con el error crudo del servidor («Internal Server Error», la
  // falla que reportó el PO). Si la prueba pasa, la pestaña —abierta en el MISMO clic para
  // que el bloqueador de ventanas no la frene— se manda a la URL firmada y el visor de PDF
  // del navegador lo muestra.
  const ver = async (docId) => {
    setAbriendo(docId);
    const pestana = window.open("", "_blank");
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      await api.get(data.url.replace(/^\/api/, ""), { responseType: "blob" });
      const directa = `${BACKEND_URL}${data.url}`;
      if (pestana) pestana.location.replace(directa);
      else window.open(directa, "_blank", "noopener");
    } catch (e) {
      if (pestana) pestana.close();
      toast.error(await mensajeFalla(e));
    } finally {
      setAbriendo("");
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3"
         data-testid="comite-capacidad-pago">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Wallet className="h-4 w-4 text-primary" />
        <p className="font-heading text-sm font-semibold text-slate-800">Capacidad de pago</p>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
              data-testid="comite-capacidad-solo-lectura">
          Solo lectura
        </span>
      </div>

      <div className="space-y-1.5">
        {docs.length === 0 && (
          <p className="text-xs text-slate-400">Sin cliente ligado a la solicitud.</p>
        )}
        {docs.map((doc) => (
          <div key={`${doc.cliente_id}-${doc.clave}`}
               data-testid={`comite-cap-doc-${doc.clave}-${doc.cliente_id}`}
               className={"flex flex-wrap items-center gap-2 rounded-md border px-2.5 py-2 text-[13px] "
                 + (doc.cargado ? "border-slate-100 bg-slate-50" : "border-dashed border-slate-300 bg-white")}>
            <span className="font-medium text-slate-800">{doc.label}</span>
            {varias && (
              <span className="text-[11px] text-slate-500">· {doc.cliente || doc.rol}</span>
            )}
            {doc.cargado ? (
              <>
                <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600">
                  {doc.origen === "solicitud" ? "De esta solicitud" : "Del expediente"}
                </span>
                <span className="text-[11px] text-slate-600">
                  Cargado: {doc.fecha_carga || "sin fecha"}
                </span>
                <span data-testid={`comite-cap-vigencia-${doc.clave}-${doc.cliente_id}`}
                  className={"rounded px-1.5 py-0.5 text-[10px] font-semibold "
                    + (!doc.vigencia_dias ? "bg-slate-200 text-slate-700"
                      : doc.vigente ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>
                  {!doc.vigencia_dias
                    ? "Sin vencimiento"
                    : doc.vigente
                      ? `Vigente${doc.vence_el ? ` hasta ${doc.vence_el}` : ""}`
                      : `Vencido${doc.vence_el ? ` el ${doc.vence_el}` : ""} (${doc.vigencia_dias} días)`}
                </span>
                <Button size="sm" variant="outline" className="ml-auto h-7 text-xs"
                  type="button" title="Ver documento"
                  disabled={abriendo === doc.doc_id} onClick={() => ver(doc.doc_id)}
                  data-testid={`comite-cap-ver-${doc.clave}-${doc.cliente_id}`}>
                  {abriendo === doc.doc_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                           : <FileDown className="mr-1 h-3 w-3" />}
                  Ver
                </Button>
              </>
            ) : (
              <span className="ml-auto flex items-center gap-1 text-[12px] font-medium text-slate-500"
                    data-testid={`comite-cap-no-cargado-${doc.clave}-${doc.cliente_id}`}>
                <FileX2 className="h-3.5 w-3.5" /> No cargado
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3"
           data-testid="comite-capacidad-datos">
        {[["Ingreso mensual declarado",
           d.ingreso_mensual_declarado ? mxn(d.ingreso_mensual_declarado) : "No capturado",
           "ingreso", d.ingreso_fuente_label],
          ["Fuente de pago", labelFuentePago(d.fuente_pago) || "No capturada", "fuente", ""],
          ["Destino del crédito", labelDestinoCredito(d.destino_credito) || "No capturado",
           "destino", ""],
          ["Origen de los recursos", d.origen_recursos || "No capturado", "origen",
           d.origen_fuente_label]].map(
          ([l, v, key, fuente]) => (
            <div key={key} className="rounded-md border border-slate-100 bg-slate-50 p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-800"
                 data-testid={`comite-cap-dato-${key}`}>{v}</p>
              {fuente ? (
                <span className="mt-1 inline-block rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600"
                      data-testid={`comite-cap-fuente-${key}`}>{fuente}</span>
              ) : null}
            </div>
          ))}
      </div>

      {/* Grupal: el ingreso y el origen de CADA integrante, con su propia procedencia. */}
      {varias && (capacidad.perfiles || []).length > 1 && (
        <div className="mt-3 space-y-1.5" data-testid="comite-cap-integrantes">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Por integrante
          </p>
          {(capacidad.perfiles || []).map((p) => (
            <div key={p.cliente_id} data-testid={`comite-cap-integrante-${p.cliente_id}`}
                 className="flex flex-wrap items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2 text-[12px]">
              <span className="font-medium text-slate-800">{p.cliente || p.rol}</span>
              <span className="text-slate-500">· {p.rol}</span>
              <span className="ml-auto flex flex-wrap items-center gap-1.5">
                <span data-testid={`comite-cap-integrante-ingreso-${p.cliente_id}`}>
                  Ingreso: <b>{p.ingreso_mensual_declarado
                    ? mxn(p.ingreso_mensual_declarado) : "No capturado"}</b>
                </span>
                {p.ingreso_fuente_label && (
                  <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600">
                    {p.ingreso_fuente_label}
                  </span>
                )}
                <span className="text-slate-300">|</span>
                <span data-testid={`comite-cap-integrante-origen-${p.cliente_id}`}>
                  Origen: <b>{p.origen_recursos || "No capturado"}</b>
                </span>
                {p.origen_fuente_label && (
                  <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600">
                    {p.origen_fuente_label}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">
        {d.ingreso_fuente_label
          ? `El ingreso declarado se tomó ${d.ingreso_fuente_label.toLowerCase()}`
          : "El ingreso declarado se busca en el perfil transaccional, la solicitud, el expediente del cliente y el prospecto ligado"}
        {d.ingreso_declarado_fecha ? ` (capturado el ${d.ingreso_declarado_fecha}` : ""}
        {d.ingreso_declarado_por ? ` por ${d.ingreso_declarado_por})` : (d.ingreso_declarado_fecha ? ")" : "")}.
        Lo que aparezca como «No cargado» o «No capturado» <b>no bloquea</b> la decisión: el
        Comité decide con lo que hay y lo deja documentado en su comentario.
      </p>
    </div>
  );
}

export default CapacidadPagoComite;
